import moment from 'moment-timezone';

export const formatDateParts = (dateString: string): { date: string; time: string } => {
  const date = moment.utc(dateString);
  const timezone = moment.tz.guess();
  const dateWithTimezone = date.tz(timezone);
  return {
    date: dateWithTimezone.format('MM/DD/YYYY'),
    time: dateWithTimezone.format('hh:mm A'),
  };
};

export const formatDate = (dateString: string, isTzSeen = false): string => {
  const date = moment.utc(dateString);
  if (!date.isValid()) return '';
  const timezone = moment.tz.guess();
  const dateWithTimezone = date.tz(timezone);
  const formattedDate = dateWithTimezone.format('MM/DD/YYYY hh:mm A (z)');
  const formattedDateWithoutTimeZone = dateWithTimezone.format('MM/DD/YYYY hh:mm A');
  return isTzSeen ? formattedDate : formattedDateWithoutTimeZone;
};

export const formatDateWithoutTZ = (dateString: string, isTzSeen = false): string => {
  const date = moment.utc(dateString);
  if (!date.isValid()) return '';
  const timezone = moment.tz.guess();
  const dateWithTimezone = date.tz(timezone);
  const formattedDate = dateWithTimezone.format('MM/DD/YYYY');
  const formattedDateWithoutTimeZone = dateWithTimezone.format('MM/DD/YYYY');
  return isTzSeen ? formattedDate : formattedDateWithoutTimeZone;
};

export const formatDateOfBirth = (dateString?: string | null): string => {
  if (!dateString) return '';
  const date = moment.utc(dateString);
  return date.isValid() ? date.format('MM/DD/YYYY') : '';
};

export const getCaseDate = (appointmentDate: string, patientState: string): string => {
  const date = moment.utc(appointmentDate).tz(patientState);

  if (date) {
    return date.format('dddd, MMMM D, YYYY');
  }

  return '';
};

export const getCaseDateForOHC = (appointmentDate: string, patientState: string): string => {
  const date = moment.utc(appointmentDate).tz(patientState);

  if (date) {
    return date.format('MMMM D, YYYY');
  }

  return '';
};

export const getCaseDateWithoutDay = (appointmentDate: string, patientState: string): string => {
  const date = moment.utc(appointmentDate).tz(patientState);

  if (date) {
    return date.format('MM/DD/YYYY');
  }

  return '';
};

export function getTimeAgo(utcDateString: string, patientState: string): string {
  const now = moment().tz(patientState);
  const date = moment.utc(utcDateString).tz(patientState);

  const diffInSeconds = now.diff(date, 'seconds');

  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

  const diffInMinutes = now.diff(date, 'minutes');
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = now.diff(date, 'hours');
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = now.diff(date, 'days');
  if (diffInDays < 30) return `${diffInDays}d ago`;

  const diffInMonths = now.diff(date, 'months');
  if (diffInMonths < 12) return `${diffInMonths}mo ago`;

  const diffInYears = now.diff(date, 'years');
  return `${diffInYears}y ago`;
}

export const getCaseTime = (appointmentDate: string, patientState: string): string => {
  const date = moment.utc(appointmentDate).tz(patientState);

  if (date) {
    return date.format('h:mm A z');
  }

  return '';
};

export const getFormatDate = (dateString: string, patientState: string): string => {
  const date = moment.utc(dateString).tz(patientState);
  if (date) {
    return date.format('MM/DD/YYYY hh:mm A');
  }

  return '';
};

export const getFormatDateFullDate = (dateString: string, patientState: string): string => {
  const date = moment.utc(dateString).tz(patientState);
  if (date.isValid()) {
    return date.format('dddd, MMMM D, YYYY, h:mmA z');
  }

  return '';
};

export const getFormattedDateSmileScan = (dateString: string, patientTimezone: string): string => {
  const date = moment.utc(dateString).tz(patientTimezone);

  if (date) {
    return date.format('dddd, MMMM D, YYYY');
  }

  return '';
};

export const getFormattedTimeSmileScan = (dateString: string, patientTimezone: string): string => {
  const date = moment.utc(dateString).tz(patientTimezone);

  if (date) {
    return date.format('h:mm A z');
  }

  return '';
};

export const formatEmail = (email: string): string => {
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1 || email.length <= 5) {
    throw new Error('Invalid email format.');
  }
  const maskedLocalPart = '*'.repeat(atIndex);
  const domain = email.slice(atIndex + 1);
  const maskedFormat = `${maskedLocalPart}@${domain}`;
  return maskedFormat;
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.length < 4) {
    throw new Error('Invalid phone number format.');
  }

  const lastFourDigits = phoneNumber.slice(-4);
  return `${'*'.repeat(phoneNumber.length - 4)}${lastFourDigits}`;
};
